<template>
  <div class="m-my">
    <!-- 骨架屏 -->
    <template v-if="loading">
      <!-- Header 骨架 -->
      <div class="m-my__header">
        <div class="m-my__header-bg"></div>
        <div class="m-my__user">
          <div class="m-my__skeleton m-my__skeleton--avatar"></div>
          <div class="m-my__user-info">
            <div class="m-my__skeleton m-my__skeleton--name"></div>
            <div class="m-my__skeleton m-my__skeleton--code"></div>
            <div class="m-my__skeleton m-my__skeleton--tag"></div>
          </div>
        </div>
      </div>
      <!-- Stats 骨架 -->
      <div class="m-my__stats">
        <div class="m-my__stat-item" v-for="n in 4" :key="n">
          <div class="m-my__skeleton m-my__skeleton--stat-num"></div>
          <div class="m-my__skeleton m-my__skeleton--stat-label"></div>
        </div>
      </div>
      <!-- Grid 骨架 -->
      <div class="m-my__grid">
        <div class="m-my__grid-item" v-for="n in 9" :key="n">
          <div class="m-my__skeleton m-my__skeleton--grid-icon"></div>
          <div class="m-my__skeleton m-my__skeleton--grid-text"></div>
        </div>
      </div>
      <!-- Settings 骨架 -->
      <div class="m-my__settings">
        <div class="m-my__skeleton m-my__skeleton--cell" v-for="n in 2" :key="n"></div>
      </div>
    </template>

    <!-- 实际内容 -->
    <template v-else>
      <!-- 顶部用户信息卡片 -->
      <div class="m-my__header">
        <div class="m-my__header-bg"></div>
        <!-- 右上角关闭按钮 -->
        <div class="m-my__close" @click="goHome">
          <van-icon name="cross" size="18" color="#fff" />
        </div>
        <div class="m-my__user">
          <div class="m-my__avatar" @click="goSettings">
            <!-- 头像显示容器 -->
            <div :class="['m-my__avatar-display', { 'audit-pending': avatarDisplay.auditStatus === 1, 'audit-rejected': avatarDisplay.auditStatus === 3 }]">
              <img v-if="avatarDisplay.src" :src="avatarDisplay.src" alt="avatar" />
              <van-icon v-else name="user-o" size="40" color="#fff" />
            </div>
            <!-- 审核状态提示 - 只在审核中和驳回时显示 -->
            <div v-if="avatarDisplay.auditStatus === 1" class="my-avatar-audit-tip">审核中</div>
            <div v-if="avatarDisplay.auditStatus === 3" class="my-avatar-audit-tip my-avatar-audit-tip--rejected">驳回</div>
          </div>
          <div class="m-my__user-info">
            <div class="m-my__user-name">{{ userName }}</div>
            <div class="m-my__user-code">{{ userType === 'company' ? '企业编号' : '用户编号' }}：{{ userCode || '--' }}</div>
            <div class="m-my__auth-tag" v-if="authStatus !== 2">
              <van-tag :type="authStatus === 0 ? 'default' : authStatus === 1 ? 'warning' : 'danger'" size="mini">
                {{ authStatusText }}
              </van-tag>
            </div>
            <van-tag v-else type="success" size="mini">已认证</van-tag>
          </div>
        </div>
      </div>

      <!-- 数据统计 -->
      <div class="m-my__stats">
        <div class="m-my__stat-item" @click="switchMenu('pending')">
          <div class="m-my__stat-num">{{ articleCounts.auditCount }}</div>
          <div class="m-my__stat-label">审核中</div>
        </div>
        <div class="m-my__stat-divider"></div>
        <div class="m-my__stat-item" @click="switchMenu('published')">
          <div class="m-my__stat-num">{{ articleCounts.publishCount }}</div>
          <div class="m-my__stat-label">已发布</div>
        </div>
        <div class="m-my__stat-divider"></div>
        <div class="m-my__stat-item" @click="switchMenu('favorite')">
          <div class="m-my__stat-num">{{ articleCounts.favoriteCount }}</div>
          <div class="m-my__stat-label">收藏</div>
        </div>
        <div class="m-my__stat-divider"></div>
        <div class="m-my__stat-item" @click="switchMenu('like')">
          <div class="m-my__stat-num">{{ articleCounts.likeCount }}</div>
          <div class="m-my__stat-label">点赞</div>
        </div>
      </div>

      <!-- 菜单宫格 -->
      <div class="m-my__grid">
        <div class="m-my__grid-item" @click="switchMenu('publish')">
          <van-icon name="edit" size="28" color="#0091EA" />
          <span>发布文章</span>
        </div>
        <div class="m-my__grid-item" @click="switchMenu('pending')">
          <van-icon name="clock-o" size="28" color="#FAAD14" />
          <span>审核中({{ articleCounts.auditCount }})</span>
        </div>
        <div class="m-my__grid-item" @click="switchMenu('rejected')">
          <van-icon name="close" size="28" color="#FF4D4F" />
          <span>已驳回({{ articleCounts.draftCount }})</span>
        </div>
        <div class="m-my__grid-item" @click="switchMenu('published')">
          <van-icon name="passed" size="28" color="#52C41A" />
          <span>已发布({{ articleCounts.publishCount }})</span>
        </div>
        <div class="m-my__grid-item" @click="switchMenu('commented')">
          <van-icon name="comment-o" size="28" color="#722ED1" />
          <span>我的评论({{ articleCounts.commentCount }})</span>
        </div>
        <div class="m-my__grid-item" @click="switchMenu('favorite')">
          <van-icon name="star-o" size="28" color="#FA8C16" />
          <span>我的收藏({{ articleCounts.favoriteCount }})</span>
        </div>
        <div class="m-my__grid-item" @click="switchMenu('like')">
          <van-icon name="good-job-o" size="28" color="#EB2F96" />
          <span>我的点赞({{ articleCounts.likeCount }})</span>
        </div>
        <div class="m-my__grid-item" @click="switchMenu('magazine')">
          <van-icon name="bookmark-o" size="28" color="#13C2C2" />
          <span>电子杂志</span>
        </div>
        <div class="m-my__grid-item" @click="switchMenu('orders')">
          <van-icon name="orders-o" size="28" color="#2F54EB" />
          <span>我的订单</span>
        </div>
      </div>

      <!-- 设置区域 -->
      <div class="m-my__settings">
        <van-cell
          v-if="userType === 'personal'"
          title="个人资料"
          icon="manager-o"
          is-link
          size="large"
          @click="goPersonalInfo"
        />
        <van-cell
          v-else
          title="企业信息"
          icon="shop-o"
          is-link
          size="large"
          @click="goCompanyInfo"
        />
        <van-cell title="退出登录" icon="cross" is-link size="large" @click="handleLogout" />
      </div>
    </template>

  </div>
</template>

<script>
import { Dialog, Toast } from 'vant'
import mobileViewport from './mixins/viewport'
import { getCompanyInfo, getCompanyIndexInfo } from '../../api/company'
import { getUserInfo, getMemberIndexInfo } from '../../api/member'

export default {
  mixins: [mobileViewport],
  name: 'MobileMy',
  data() {
    const userType = localStorage.getItem('userType') || 'personal'
    return {
      userType,
      userName: '用户',
      userAvatar: '',
      // 新头像审核状态相关字段
      newAvatar: '',
      newAvatarAuditStatus: 0,
      userCode: '',
      authStatus: 0,
      articleCounts: {
        auditCount: 0,
        draftCount: 0,
        publishCount: 0,
        likeCount: 0,
        favoriteCount: 0,
        commentCount: 0
      },
      loading: true
    }
  },
  computed: {
    authStatusText() {
      const map = { 0: '未认证', 1: '待审核', 2: '已认证', 3: '认证失败' }
      return map[this.authStatus] || '未认证'
    },
    /**
     * 头像显示逻辑
     * - newAvatarAuditStatus 为 1 时显示 newAvatar（审核中）
     * - newAvatarAuditStatus 为 3 时显示 newAvatar（驳回）
     * - 其他情况显示 userAvatar
     */
    avatarDisplay() {
      const auditStatus = this.newAvatarAuditStatus
      let src = ''
      
      if (auditStatus === 1 || auditStatus === 3) {
        // 审核中和驳回都显示新头像
        src = this.newAvatar || ''
      } else {
        // 其他情况显示原头像
        src = this.userAvatar || ''
      }
      
      return {
        src,
        auditStatus
      }
    }
  },
  created() {
    Toast.setDefaultOptions({ position: 'top' })
    this.loadAll()
  },
  beforeDestroy() {
    Toast.resetDefaultOptions()
  },
  methods: {
    async loadAll() {
      this.loading = true
      try {
        await Promise.all([
          this.loadWelcomeInfo(),
          this.loadIndexInfo()
        ])
      } finally {
        this.loading = false
      }
    },
    async loadWelcomeInfo() {
      if (this.userType === 'company') {
        try {
          const res = await getCompanyInfo()
          const data = res.data || {}
          this.userName = data.companyShortName || data.companyName || '企业用户'
          this.userAvatar = data.avatar || ''
          // 新头像审核状态相关字段
          this.newAvatar = data.newAvatar || ''
          this.newAvatarAuditStatus = data.newAvatarAuditStatus || 0
          this.authStatus = data.auditStatus || 0
          this.userCode = data.companyCode || ''
          localStorage.setItem('authStatus', String(this.authStatus))
        } catch (err) { /* ignore */ }
      } else {
        try {
          const res = await getUserInfo()
          const data = res.data || {}
          this.userName = data.nickname || '用户'
          this.userAvatar = data.avatar || ''
          // 新头像审核状态相关字段
          this.newAvatar = data.newAvatar || ''
          this.newAvatarAuditStatus = data.newAvatarAuditStatus || 0
          this.authStatus = data.realNameStatus || 0
          this.userCode = data.userCode || ''
          localStorage.setItem('authStatus', String(this.authStatus))
          const userInfoStr = localStorage.getItem('userInfo')
          const userInfo = userInfoStr ? JSON.parse(userInfoStr) : {}
          userInfo.nickname = data.nickname || userInfo.nickname
          userInfo.avatar = data.avatar || userInfo.avatar
          localStorage.setItem('userInfo', JSON.stringify(userInfo))
        } catch (err) { /* ignore */ }
      }
    },
    async loadIndexInfo() {
      const api = this.userType === 'company' ? getCompanyIndexInfo : getMemberIndexInfo
      try {
        const res = await api()
        const data = res.data || {}
        this.articleCounts = {
          auditCount: data.auditCount || 0,
          draftCount: data.draftCount || 0,
          publishCount: data.publishCount || 0,
          likeCount: data.likeCount || 0,
          favoriteCount: data.favoriteCount || 0,
          commentCount: data.commentCount || 0
        }
      } catch (err) { /* ignore */ }
    },
    switchMenu(menu) {
      if (menu === 'publish') {
        // 检查认证状态：realNameStatus 不为 2 时不允许发布
        if (this.authStatus !== 2) {
          const message = this.userType === 'company'
            ? '没有进行企业认证无法发布文章' 
            : '没有进行实名认证无法发布文章'
          Toast(message)
          return
        }
        this.$router.push({ name: 'mobile-publish' })
        return
      }
      if (menu === 'magazine') {
        this.$router.push({ name: 'mobile-magazine' })
        return
      }
      if (menu === 'orders') {
        this.$router.push({ name: 'mobile-orders' })
        return
      }
      // 审核中、已驳回、已发布、留言、收藏、点赞 → 跳转列表页
      this.$router.push({ name: 'mobile-articles', params: { tab: menu } })
    },
    goPersonalInfo() {
      this.$router.push({ name: 'mobile-personal' })
    },
    goCompanyInfo() {
      this.$router.push({ name: 'mobile-company' })
    },
    goSettings() {
      if (this.userType === 'personal') {
        this.goPersonalInfo()
      } else {
        this.goCompanyInfo()
      }
    },
    goHome() {
      this.$router.push('/home')
    },
    handleLogout() {
      Dialog.confirm({
        title: '提示',
        message: '是否退出登录？',
        confirmButtonText: '确认',
        cancelButtonText: '取消'
      }).then(() => {
        localStorage.removeItem('token')
        localStorage.removeItem('userType')
        localStorage.removeItem('userInfo')
        this.$router.push('/')
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.m-my {
  min-height: 100vh;
  background: #F5F7FA;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* ========== Header ========== */
.m-my__header {
  position: relative;
}

.m-my__header-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 170px;
  background: linear-gradient(135deg, #1565c0 0%, #42a5f5 100%);
  border-radius: 0 0 20px 20px;
}

.m-my__close {
  position: absolute;
  top: 14px;
  right: 16px;
  z-index: 10;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 50%;
  backdrop-filter: blur(4px);
}

.m-my__close:active {
  background: rgba(0, 0, 0, 0.4);
}

.m-my__user {
  position: relative;
  display: flex;
  align-items: center;
  padding: 40px 20px 30px;
  gap: 16px;
}

.m-my__avatar {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
}

/* 头像显示容器 */
.m-my__avatar-display {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
}

.m-my__avatar img,
.m-my__avatar .m-my__avatar-display {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.m-my__avatar:hover .my-avatar-audit-tip {
  opacity: 1;
}

/* 头像审核状态提示 */
.my-avatar-audit-tip {
  position: absolute;
  top: -2px;
  left: 50%;
  transform: translateX(-50%);
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.4;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  opacity: 1;
}

/* 审核中 - 蓝色 */
.my-avatar-audit-tip:not(.my-avatar-audit-tip--rejected) {
  background: rgba(225, 243, 248, 0.9);
  color: #23B4DC;
}

/* 驳回 - 红色 */
.my-avatar-audit-tip--rejected {
  background: rgba(255, 241, 240, 0.9);
  color: #FF4D4F;
}

.m-my__user-info {
  flex: 1;
}

.m-my__user-name {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
}

.m-my__user-code {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 4px;
}

.m-my__auth-tag {
  display: inline-block;
}

/* ========== Stats ========== */
.m-my__stats {
  display: flex;
  align-items: center;
  background: #fff;
  margin: 0 16px;
  padding: 16px 0;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  position: relative;
  z-index: 1;
  margin-top: 40px;
}

.m-my__stat-item {
  flex: 1;
  text-align: center;
}

.m-my__stat-num {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
}

.m-my__stat-label {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.m-my__stat-divider {
  width: 1px;
  height: 28px;
  background: #eee;
}

/* ========== Grid ========== */
.m-my__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: #eee;
  margin: 16px;
  border-radius: 16px;
  overflow: hidden;
}

.m-my__grid-item {
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 18px 8px;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.m-my__grid-item:active {
  background: #F5F7FA;
}

.m-my__grid-item span {
  font-size: 12px;
  color: #666;
  text-align: center;
}

/* ========== Settings ========== */
.m-my__settings {
  margin: 16px;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
}

.m-my__settings .van-cell {
  padding: 16px;
}

.m-my__settings .van-cell:not(:last-child)::after {
  left: 48px;
}

/* ========== 骨架屏 ========== */
.m-my__skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: my-skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes my-skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.m-my__skeleton--avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  flex-shrink: 0;
}

.m-my__skeleton--name {
  width: 120px;
  height: 20px;
  margin-bottom: 8px;
}

.m-my__skeleton--code {
  width: 160px;
  height: 14px;
  margin-bottom: 8px;
}

.m-my__skeleton--tag {
  width: 50px;
  height: 18px;
}

.m-my__skeleton--stat-num {
  width: 40px;
  height: 22px;
  margin: 0 auto 4px;
}

.m-my__skeleton--stat-label {
  width: 40px;
  height: 12px;
  margin: 0 auto;
}

.m-my__skeleton--grid-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
}

.m-my__skeleton--grid-text {
  width: 60px;
  height: 12px;
}

.m-my__skeleton--cell {
  width: 100%;
  height: 52px;
}
</style>
